import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    RefreshControl,
    ActivityIndicator,
    StatusBar,
    Platform,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import axios from 'axios';
import { Feather } from '@expo/vector-icons';
import { COLORS, API, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';

export default function UndianScreen() {
    const navigation = useNavigation();
    const { user, refreshUser } = useAuth();
    const [undianList, setUndianList] = useState([]);
    const [groupedUndian, setGroupedUndian] = useState({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchUndian = async () => {
        try {
            const response = await axios.get(API.UNDIAN.LIST(user.id), { timeout: 15000 });
            if (response.data.success) {
                setUndianList(response.data.data || []);
                setGroupedUndian(response.data.grouped || {});
            }
        } catch (error) {
            console.log('Fetch undian error:', error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            refreshUser();
            fetchUndian();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        refreshUser();
        fetchUndian();
    };

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusStyle = (status) => {
        const statusStyles = {
            active: { bg: COLORS.success + '15', color: COLORS.success, text: 'Aktif', icon: 'check-circle' },
            winner: { bg: COLORS.gold + '20', color: '#DAA520', text: 'Menang!', icon: 'award' },
            expired: { bg: COLORS.textMuted + '15', color: COLORS.textMuted, text: 'Kadaluarsa', icon: 'clock' },
        };
        return statusStyles[status] || statusStyles.active;
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    const periodes = Object.keys(groupedUndian).sort().reverse();

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
                    <Feather name="arrow-left" size={22} color={COLORS.textPrimary} />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Undian Saya</Text>
                <View style={styles.poinBadge}>
                    <Text style={styles.poinValue}>{formatNumber(user?.poin)} MP</Text>
                </View>
            </View>

            {/* Stats Card */}
            <View style={styles.statsCard}>
                <View style={styles.statItem}>
                    <Text style={styles.statIcon}>🎫</Text>
                    <Text style={styles.statValue}>{undianList.length}</Text>
                    <Text style={styles.statLabel}>Total Undian</Text>
                </View>
                <View style={styles.statDivider} />
                <View style={styles.statItem}>
                    <Text style={styles.statIcon}>✅</Text>
                    <Text style={styles.statValue}>{undianList.filter(u => u.status === 'active').length}</Text>
                    <Text style={styles.statLabel}>Aktif</Text>
                </View>
                <View style={styles.statDivider} />
                <View style={styles.statItem}>
                    <Text style={styles.statIcon}>🏆</Text>
                    <Text style={styles.statValue}>{undianList.filter(u => u.status === 'winner').length}</Text>
                    <Text style={styles.statLabel}>Menang</Text>
                </View>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={COLORS.primary}
                    />
                }
            >
                {/* Info Box */}
                <View style={styles.infoBox}>
                    <Feather name="info" size={18} color={COLORS.primary} />
                    <Text style={styles.infoText}>
                        Undian diundi setiap akhir bulan. Semakin banyak lembar undian, semakin besar peluang menang!
                    </Text>
                </View>

                {undianList.length === 0 ? (
                    <View style={styles.emptyState}>
                        <Text style={styles.emptyIcon}>🎟️</Text>
                        <Text style={styles.emptyTitle}>Belum Ada Undian</Text>
                        <Text style={styles.emptyText}>Tukarkan poin Anda dengan undian di menu Hadiah</Text>
                        <TouchableOpacity
                            style={styles.emptyBtn}
                            onPress={() => navigation.navigate('Hadiah')}
                        >
                            <Feather name="gift" size={16} color="#fff" />
                            <Text style={styles.emptyBtnText}>Tukar Undian</Text>
                        </TouchableOpacity>
                    </View>
                ) : (
                    periodes.map((periode) => (
                        <View key={periode} style={styles.periodeSection}>
                            <View style={styles.periodeHeader}>
                                <Feather name="calendar" size={16} color={COLORS.primary} />
                                <Text style={styles.periodeTitle}>{periode}</Text>
                                <View style={styles.periodeBadge}>
                                    <Text style={styles.periodeBadgeText}>
                                        {groupedUndian[periode].length} lembar
                                    </Text>
                                </View>
                            </View>

                            {groupedUndian[periode].map((item) => {
                                const status = getStatusStyle(item.status);
                                return (
                                    <View key={item.id} style={styles.undianCard}>
                                        <View style={styles.ticketIcon}>
                                            <Text style={styles.ticketEmoji}>🎟️</Text>
                                        </View>
                                        <View style={styles.undianContent}>
                                            <Text style={styles.undianNumber}>{item.nomor_undian}</Text>
                                            <Text style={styles.undianDate}>{formatDate(item.created_at)}</Text>
                                        </View>
                                        <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                                            <Feather name={status.icon} size={12} color={status.color} />
                                            <Text style={[styles.statusText, { color: status.color }]}>
                                                {status.text}
                                            </Text>
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    ))
                )}

                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: Platform.OS === 'web' ? 20 : 50,
        paddingBottom: 16,
        paddingHorizontal: 16,
        backgroundColor: COLORS.bgDarker,
    },
    backBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    poinBadge: {
        backgroundColor: COLORS.primary + '15',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
    },
    poinValue: {
        fontSize: 13,
        color: COLORS.primary,
        fontWeight: '700',
    },
    statsCard: {
        flexDirection: 'row',
        backgroundColor: COLORS.bgCard,
        marginHorizontal: 16,
        marginTop: 16,
        borderRadius: 16,
        padding: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    statItem: {
        flex: 1,
        alignItems: 'center',
    },
    statIcon: {
        fontSize: 24,
        marginBottom: 4,
    },
    statValue: {
        fontSize: 20,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    statLabel: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginTop: 2,
    },
    statDivider: {
        width: 1,
        backgroundColor: COLORS.border,
        marginVertical: 8,
    },
    scrollView: {
        flex: 1,
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    infoBox: {
        flexDirection: 'row',
        backgroundColor: COLORS.primary + '10',
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
        alignItems: 'flex-start',
        gap: 10,
        borderWidth: 1,
        borderColor: COLORS.primary + '20',
    },
    infoText: {
        flex: 1,
        fontSize: 12,
        color: COLORS.textSecondary,
        lineHeight: 18,
    },
    periodeSection: {
        marginBottom: 20,
    },
    periodeHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
        gap: 8,
    },
    periodeTitle: {
        flex: 1,
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.textPrimary,
    },
    periodeBadge: {
        backgroundColor: COLORS.primary + '15',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 10,
    },
    periodeBadgeText: {
        fontSize: 11,
        color: COLORS.primary,
        fontWeight: '600',
    },
    undianCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 12,
        padding: 14,
        marginBottom: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03,
        shadowRadius: 4,
        elevation: 1,
    },
    ticketIcon: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    ticketEmoji: {
        fontSize: 22,
    },
    undianContent: {
        flex: 1,
    },
    undianNumber: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 2,
        fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    undianDate: {
        fontSize: 11,
        color: COLORS.textMuted,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8,
        gap: 4,
    },
    statusText: {
        fontSize: 11,
        fontWeight: '600',
    },
    emptyState: {
        alignItems: 'center',
        padding: 40,
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        marginTop: 20,
    },
    emptyIcon: {
        fontSize: 48,
        marginBottom: 12,
        opacity: 0.6,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 6,
    },
    emptyText: {
        color: COLORS.textSecondary,
        fontSize: 13,
        textAlign: 'center',
        marginBottom: 16,
    },
    emptyBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.primary,
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 12,
        gap: 8,
    },
    emptyBtnText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 14,
    },
});
